"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import { videoPosterUrl } from "@/lib/video-poster"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { useEditRegion } from "@/lib/edit/edit-region-registry"

interface VideoTestimonialProps {
  previewSrc: string
  fullSrc: string
  posterSrc: string
  title: string
  subtitle: string
  className?: string
  /** Aspect-ratio utility for the tile. Default portrait 9/16; callers can pass
   *  a shorter ratio (e.g. `aspect-[4/5]`) to crop the tile height. */
  aspectClass?: string
  /** Payload testimonial doc id — registers the inline-editor regions for the
   *  `authorName` (title) + `authorTitle` (subtitle). Absent → no regions. */
  cmsId?: string
  /** BCP47 CMS locale these docs were fetched in (region provenance). */
  editLocale?: string
}

export function VideoTestimonial({
  previewSrc,
  fullSrc,
  posterSrc,
  title,
  subtitle,
  className,
  aspectClass = "aspect-[9/16]",
  cmsId,
  editLocale,
}: VideoTestimonialProps) {
  const ut = useTranslations("ui")
  const titleRef = useEditRegion<HTMLParagraphElement>(
    cmsId && editLocale
      ? {
          collection: "testimonials",
          id: cmsId,
          field: "authorName",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const subtitleRef = useEditRegion<HTMLParagraphElement>(
    cmsId && editLocale
      ? {
          collection: "testimonials",
          id: cmsId,
          field: "authorTitle",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLVideoElement>(null)
  const fullRef = useRef<HTMLVideoElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)

  // Lazy-load: only start video when scrolled into view
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          obs.unobserve(el)
        }
      },
      { threshold: 0.3 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  // When switching to full video, play from start with audio
  const playFull = () => {
    setIsPlaying(true)
    // Small delay to let the element render
    setTimeout(() => {
      const vid = fullRef.current
      if (vid) {
        vid.currentTime = 0
        vid.muted = false
        vid.play().catch(() => {
          // Autoplay with sound blocked — try muted
          vid.muted = true
          vid.play().catch(() => {})
        })
      }
    }, 50)
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative h-full max-w-full overflow-hidden rounded-[12px] bg-neutral-200",
        aspectClass,
        className
      )}
    >
      {!isPlaying ? (
        <>
          {/* Muted autoplay loop preview */}
          {isVisible ? (
            <video
              ref={previewRef}
              src={previewSrc}
              poster={videoPosterUrl(posterSrc, 300, 533)}
              muted
              autoPlay
              loop
              playsInline
              // iOS: block long-press share sheet + disable download context menu
              controlsList="nodownload"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              style={{ WebkitTouchCallout: "none" }}
              className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <Image
              src={posterSrc}
              alt=""
              fill
              sizes="(min-width: 768px) 300px, 50vw"
              className="object-cover"
            />
          )}

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* YouTube-style channel badge */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <svg
              width="28"
              height="20"
              viewBox="0 0 28 20"
              fill="none"
              aria-hidden="true"
            >
              <rect width="28" height="20" rx="5" fill="#FF0000" />
              <path d="M12 5.5l6 4.5-6 4.5V5.5z" fill="white" />
            </svg>
            <span className="type-title-md text-white">Ahead</span>
          </div>

          {/* Play button — sized to the circle so vertical swipes outside it
              pass through to the page scroll instead of being intercepted. */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <button
              type="button"
              onClick={playFull}
              className="pointer-events-auto flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-colors hover:bg-white/30"
              aria-label={`Play video: ${title}`}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>

          {/* Title + subtitle */}
          <div className="absolute right-0 bottom-0 left-0 p-5">
            <p
              ref={titleRef}
              className="type-title-md leading-snug font-medium text-white"
            >
              {title}
            </p>
            <p
              ref={subtitleRef}
              className="type-body-md mt-1 line-clamp-3 leading-snug text-white/70"
            >
              {subtitle}
            </p>
          </div>
        </>
      ) : (
        /* Full video with controls */
        <video
          ref={fullRef}
          src={fullSrc}
          controls
          playsInline
          data-exclusive-audio="true"
          onPlay={(e) => {
            const self = e.currentTarget
            document
              .querySelectorAll<HTMLVideoElement>(
                'video[data-exclusive-audio="true"]'
              )
              .forEach((v) => {
                if (v !== self) v.pause()
              })
          }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* Announce the preview→full-video swap to screen readers. The text
          only renders while playing, so toggling back to the preview clears
          the live region without re-announcing. */}
      <div role="status" aria-live="polite" className="sr-only">
        {isPlaying ? `${ut("videoPlaying")}: ${title}` : ""}
      </div>
    </div>
  )
}
